// ─── Professional DOCX Exporter ─────────────────────────────────────────────
// Uses the `docx` npm package to generate real .docx files.
// Converts chapter HTML → docx elements, embeds figure metadata, builds
// proper front matter, TOC, LOF, and LOT.

import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
  PageBreak, Table, TableRow, TableCell, WidthType, BorderStyle,
  Header, Footer, PageNumber, NumberFormat, SectionType,
  convertMillimetersToTwip, LevelFormat, convertInchesToTwip,
  TableOfContents, ImageRun,
} from 'docx'
import { sanitizeChapterContent } from './contentSanitizer'
import { composeThesisDocument, stripLeadingFigureNumber } from './exportCompositionEngine'
import { renderMermaidSvg, sizeSvg } from './export/mermaidRenderer'
import { safeDecode } from './exportCompositionEngine.js'

// ── SVG → PNG conversion (browser canvas) ────────────────────────────────────

async function svgToPngArrayBuffer(svgString, width = 560, height = 420) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null
  return new Promise((resolve) => {
    // An <img> that never fires load or error leaves this promise pending
    // forever, and the export await-chain hangs with no error and no download.
    // Every figure gets a hard ceiling instead.
    let settled = false
    const finish = (value) => { if (!settled) { settled = true; resolve(value) } }
    const timer = setTimeout(() => {
      console.error('[docxExporter] figure rasterisation timed out after 10s')
      finish(null)
    }, 10000)
    const resolve_ = finish
    try {
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
      const url  = URL.createObjectURL(blob)
      const img  = new window.Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width  = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, width, height)
        ctx.drawImage(img, 0, 0, width, height)
        URL.revokeObjectURL(url)
        clearTimeout(timer)
        canvas.toBlob(pngBlob => {
          if (!pngBlob) { resolve_(null); return }
          pngBlob.arrayBuffer().then(buf => resolve_(buf)).catch(() => resolve_(null))
        }, 'image/png')
      }
      img.onerror = () => { URL.revokeObjectURL(url); clearTimeout(timer); resolve_(null) }
      img.src = url
    } catch { resolve_(null) }
  })
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TIMES_NEW_ROMAN = 'Times New Roman'
const FONT_SIZE_BODY  = 24 // half-points: 24 = 12pt
const FONT_SIZE_H1    = 28 // 14pt
const FONT_SIZE_H2    = 24 // 12pt
const FONT_SIZE_H3    = 24 // 12pt

// Margins in twips (1 inch = 1440 twips)
const MARGIN = {
  top:    convertInchesToTwip(1),
  bottom: convertInchesToTwip(1),
  left:   convertInchesToTwip(1.5), // wider for binding
  right:  convertInchesToTwip(1),
}

// ── Text helpers ─────────────────────────────────────────────────────────────

function run(text, opts = {}) {
  return new TextRun({
    text,
    font:   TIMES_NEW_ROMAN,
    size:   FONT_SIZE_BODY,
    bold:   opts.bold   || false,
    italics:opts.italic || false,
    ...opts,
  })
}

function emptyLine() {
  return new Paragraph({ children: [run('')], spacing: { line: 480 } })
}

// ── HTML to DOCX converter ────────────────────────────────────────────────────

/**
 * Parse HTML string (browser DOMParser) into an array of docx Paragraphs/Tables.
 * Handles: h1–h4, p, strong, em, ul, ol, li, table, blockquote, hr, figure tokens,
 * and chart-svg figure tokens (converted to embedded PNG images).
 */
async function htmlToDocxElements(html, figureRegistry, tableRegistry, ordered = null) {
  // Token ids collide (fig_<chapterNumber>_<n>, with chapterNumber defaulting to
  // 1 when the chapter record has not loaded), so a Map keyed by id mislabels
  // figures. When the caller supplies this chapter's ordered lists, match by
  // position instead — that cannot collide.
  let figSeq = 0
  let tblSeq = 0
  if (typeof window === 'undefined') return [new Paragraph({ children: [run(html)] })]

  const parser  = new DOMParser()
  const doc     = parser.parseFromString(`<div>${html}</div>`, 'text/html')
  const root    = doc.querySelector('div')
  const result  = []

  async function processNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return null
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const tag = node.tagName.toLowerCase()

    // ── Figure token ──────────────────────────────────────────────────────
    if (node.dataset?.thesisFigure === 'true') {
      const token   = node.dataset.figureId || ''
      const type    = node.dataset.figureType || 'mermaid'
      const title   = stripLeadingFigureNumber(safeDecode(node.dataset.figureTitle || ''))
      const caption = safeDecode(node.dataset.figureCaption || '')

      // Positional match first, registry by id only as a fallback.
      const isTable    = type === 'table'
      const positional = ordered
        ? (isTable ? ordered.tables?.[tblSeq++] : ordered.figures?.[figSeq++])
        : null
      const reg   = positional || (isTable ? tableRegistry.get(token) : figureRegistry.get(token))
      const label = reg?.displayLabel || (isTable ? `Table: ${title}` : `Figure: ${title}`)

      if (type === 'chart-svg') {
        // Research Lab SVG chart — convert to PNG and embed as image
        const svgCode = safeDecode(node.dataset.figureCode || '')
        if (svgCode) {
          try {
            const pngBuffer = await svgToPngArrayBuffer(svgCode, 560, 420)
            if (pngBuffer) {
              result.push(emptyLine())
              result.push(new Paragraph({
                children: [new ImageRun({
                  data: pngBuffer,
                  type: 'png',
                  transformation: { width: 400, height: 300 },
                })],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200, after: 60 },
              }))
            } else {
              result.push(new Paragraph({ children: [run(`[${label || title || 'Chart Figure'}]`, { bold: true })], alignment: AlignmentType.CENTER }))
            }
          } catch {
            result.push(new Paragraph({ children: [run(`[${label || title || 'Chart Figure'}]`, { bold: true })], alignment: AlignmentType.CENTER }))
          }
        }
        if (title) result.push(new Paragraph({ children: [run(title, { bold: true })], alignment: AlignmentType.CENTER, spacing: { before: 40, after: 40 } }))
        if (caption) result.push(new Paragraph({ children: [run(caption, { italic: true, size: 20 })], alignment: AlignmentType.CENTER, spacing: { before: 0, after: 200 } }))
        result.push(emptyLine())
      } else if (type === 'table') {
        // Render table as DOCX table
        const code = safeDecode(node.dataset.figureCode || '')
        const tableEl = markdownTableToDocx(code, label, caption)
        result.push(...tableEl)
      } else {
        // Mermaid diagram. This used to emit a bare "[Figure 2.1]" text box on
        // the assumption that diagrams need a server-side renderer; they do not.
        // mermaid is bundled and the export runs in the browser, so render to
        // SVG and reuse the same SVG→PNG path the charts already use.
        const mermaidCode = safeDecode(node.dataset.figureCode || '')
        let embedded = false

        if (mermaidCode) {
          try {
            const rawSvg = await renderMermaidSvg(mermaidCode)
            if (rawSvg) {
              const { svg, width, height } = sizeSvg(rawSvg, 560)
              const pngBuffer = await svgToPngArrayBuffer(svg, width, height)
              if (pngBuffer) {
                result.push(emptyLine())
                result.push(new Paragraph({
                  children: [new ImageRun({
                    data: pngBuffer,
                    type: 'png',
                    transformation: { width: Math.round(width * 0.72), height: Math.round(height * 0.72) },
                  })],
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 200, after: 60 },
                }))
                embedded = true
              }
            }
          } catch (err) {
            console.error('[docxExporter] mermaid embed failed:', err?.message)
          }
        }

        // Fall back to the labelled box only when rendering genuinely failed, so
        // a broken diagram still leaves a visible, numbered placeholder rather
        // than a silent gap.
        if (!embedded) {
          result.push(
            new Paragraph({
              children: [run(`[${label}]`, { bold: true })],
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 60 },
              border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'AAAAAA' }, bottom: { style: BorderStyle.SINGLE, size: 1, color: 'AAAAAA' } },
            })
          )
        } else {
          result.push(new Paragraph({
            children: [run(label, { bold: true })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 40 },
          }))
        }
        if (title) {
          result.push(new Paragraph({
            children: [run(title, { bold: true })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 60, after: 40 },
          }))
        }
        if (caption) {
          result.push(new Paragraph({
            children: [run(caption, { italic: true, size: 20 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 200 },
          }))
        }
      }
      return null
    }

    // ── Headings ──────────────────────────────────────────────────────────
    if (['h1','h2','h3','h4'].includes(tag)) {
      const text  = node.textContent.trim()
      if (!text) return null
      const level = { h1: HeadingLevel.HEADING_1, h2: HeadingLevel.HEADING_2, h3: HeadingLevel.HEADING_3, h4: HeadingLevel.HEADING_4 }[tag]
      const isCh  = tag === 'h1'
      result.push(new Paragraph({
        heading:   level,
        alignment: isCh ? AlignmentType.CENTER : AlignmentType.LEFT,
        spacing:   { before: isCh ? 240 : 200, after: 120 },
        children:  [new TextRun({
          text,
          font:      TIMES_NEW_ROMAN,
          size:      isCh ? FONT_SIZE_H1 : FONT_SIZE_H2,
          bold:      true,
          allCaps:   isCh,
          italics:   tag === 'h3',
        })],
        pageBreakBefore: tag === 'h1',
      }))
      return null
    }

    // ── Paragraph ─────────────────────────────────────────────────────────
    if (tag === 'p') {
      const runs = inlineRuns(node)
      if (runs.length === 0) return null
      result.push(new Paragraph({
        children:  runs,
        alignment: AlignmentType.JUSTIFIED,
        spacing:   { line: 480, after: 0 }, // double-spaced
        indent:    { firstLine: convertInchesToTwip(0.5) },
      }))
      return null
    }

    // ── Blockquote ────────────────────────────────────────────────────────
    if (tag === 'blockquote') {
      result.push(new Paragraph({
        children:  [run(node.textContent.trim(), { italic: true })],
        alignment: AlignmentType.JUSTIFIED,
        indent:    { left: convertInchesToTwip(0.75), right: convertInchesToTwip(0.5) },
        spacing:   { before: 120, after: 120, line: 360 },
      }))
      return null
    }

    // ── Lists ─────────────────────────────────────────────────────────────
    if (tag === 'ul' || tag === 'ol') {
      const ordered = tag === 'ol'
      let itemNum   = 1
      for (const li of node.querySelectorAll(':scope > li')) {
        const prefix = ordered ? `${itemNum++}. ` : '• '
        result.push(new Paragraph({
          children:  [run(prefix), ...inlineRuns(li)],
          indent:    { left: convertInchesToTwip(0.75), hanging: convertInchesToTwip(0.25) },
          spacing:   { line: 400, after: 40 },
        }))
      }
      return null
    }

    // ── Table ─────────────────────────────────────────────────────────────
    if (tag === 'table') {
      const docxTable = htmlTableToDocx(node)
      if (docxTable) result.push(docxTable)
      result.push(emptyLine())
      return null
    }

    // ── Horizontal rule ───────────────────────────────────────────────────
    if (tag === 'hr') {
      result.push(new Paragraph({
        children: [run('')],
        border:   { bottom: { style: BorderStyle.SINGLE, size: 1, color: '999999' } },
        spacing:  { before: 120, after: 120 },
      }))
      return null
    }

    // ── Figure/div containers — recurse ──────────────────────────────────
    if (['div', 'figure', 'section', 'article'].includes(tag)) {
      for (const child of node.childNodes) await processNode(child)
      return null
    }

    // ── Unknown block — extract text as paragraph ─────────────────────────
    const text = node.textContent.trim()
    if (text) {
      result.push(new Paragraph({ children: [run(text)], alignment: AlignmentType.JUSTIFIED, spacing: { line: 480 } }))
    }
    return null
  }

  for (const child of root.childNodes) await processNode(child)
  return result
}

// ── Inline run builder ────────────────────────────────────────────────────────

function inlineRuns(node) {
  const runs = []
  function walk(n, bold = false, italic = false) {
    if (n.nodeType === Node.TEXT_NODE) {
      const text = n.textContent
      if (text) runs.push(new TextRun({ text, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold, italics: italic }))
      return
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return
    const t = n.tagName.toLowerCase()
    const b = bold   || ['strong','b'].includes(t)
    const i = italic || ['em','i'].includes(t)
    if (['br'].includes(t)) { runs.push(new TextRun({ break: 1 })); return }
    for (const child of n.childNodes) walk(child, b, i)
  }
  for (const child of node.childNodes) walk(child)
  return runs
}

// ── HTML table → DOCX table ───────────────────────────────────────────────────

function htmlTableToDocx(tableNode) {
  const rows = []
  for (const tr of tableNode.querySelectorAll('tr')) {
    const cells = []
    for (const cell of tr.querySelectorAll('th, td')) {
      const isTh = cell.tagName.toLowerCase() === 'th'
      cells.push(new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: cell.textContent.trim(), font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold: isTh })],
          alignment: isTh ? AlignmentType.CENTER : AlignmentType.LEFT,
        })],
        shading: isTh ? { fill: 'F0ECE4', color: '000000' } : undefined,
      }))
    }
    if (cells.length > 0) rows.push(new TableRow({ children: cells }))
  }
  if (rows.length === 0) return null
  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top:           { style: BorderStyle.SINGLE, size: 1, color: '999999' },
      bottom:        { style: BorderStyle.SINGLE, size: 1, color: '999999' },
      left:          { style: BorderStyle.SINGLE, size: 1, color: '999999' },
      right:         { style: BorderStyle.SINGLE, size: 1, color: '999999' },
      insideH:       { style: BorderStyle.SINGLE, size: 1, color: '999999' },
      insideV:       { style: BorderStyle.SINGLE, size: 1, color: '999999' },
    },
  })
}

// ── Markdown table → DOCX elements ───────────────────────────────────────────

function markdownTableToDocx(code, label, caption) {
  const lines = (code || '').trim().split('\n').filter(l => l.trim() && !l.match(/^\s*[-|:]+\s*$/))
  const parseRow = (line) => line.split('|').map(c => c.trim()).filter(Boolean)

  const result = []
  if (label) {
    result.push(new Paragraph({
      children:  [run(label, { bold: true })],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 200, after: 80 },
    }))
  }

  if (lines.length > 0) {
    const rows = []
    for (let i = 0; i < lines.length; i++) {
      const cells = parseRow(lines[i]).map((text, j) =>
        new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ text, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold: i === 0 })],
            alignment: i === 0 ? AlignmentType.CENTER : AlignmentType.LEFT,
          })],
          shading: i === 0 ? { fill: 'F0ECE4' } : undefined,
        })
      )
      if (cells.length > 0) rows.push(new TableRow({ children: cells }))
    }
    if (rows.length > 0) {
      result.push(new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
          insideH: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
          insideV: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
        },
      }))
    }
  }

  if (caption) {
    result.push(new Paragraph({
      children:  [run(caption, { italic: true, size: 20 })],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 80, after: 200 },
    }))
  }

  return result
}

// ── Front matter section builders ─────────────────────────────────────────────

function buildFrontMatterParagraphs(fm, thesis) {
  const t = (text, opts = {}) => new TextRun({ text: text || '', font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, ...opts })

  const val = (key) => (fm?.[key] && String(fm[key]).trim()) ? String(fm[key]).trim() : null

  const sections = []

  // ── Title page ──────────────────────────────────────────────────────────
  sections.push([
    new Paragraph({ children: [t('')], pageBreakBefore: true }),
    new Paragraph({
      children:  [t(thesis?.title || '', { bold: true, size: 32, allCaps: true })],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 1440, after: 480 },
    }),
    new Paragraph({
      children:  [t('A Thesis Submitted in Partial Fulfilment of the Requirements for the Degree of')],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 0, after: 240 },
    }),
    new Paragraph({
      children:  [t(`${val('degree') || 'Doctor of Philosophy'} in ${val('specialization') || ''}`, { bold: true })],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 0, after: 480 },
    }),
    new Paragraph({
      children:  [t('Submitted by:'), new TextRun({ break: 1 }), t(val('student_name') || '', { bold: true }), new TextRun({ break: 1 }), t(`Registration No.: ${val('reg_number') || ''}`)],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 0, after: 480 },
    }),
    new Paragraph({
      children:  [t('Under the Supervision of:'), new TextRun({ break: 1 }), t(val('supervisor_name') || '', { bold: true }), new TextRun({ break: 1 }), t(val('supervisor_designation') || ''), new TextRun({ break: 1 }), t(val('supervisor_institution') || '')],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 0, after: 720 },
    }),
    new Paragraph({
      children:  [t(val('university_name') || '', { bold: true }), new TextRun({ break: 1 }), t(val('faculty_name') || ''), new TextRun({ break: 1 }), t(`${val('submission_month') || ''} ${val('submission_year') || ''}`.trim())],
      alignment: AlignmentType.CENTER,
      spacing:   { before: 0, after: 0 },
    }),
  ])

  // ── Certificate ──────────────────────────────────────────────────────────
  sections.push([
    new Paragraph({ children: [t('CERTIFICATE', { bold: true, size: 28, allCaps: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    new Paragraph({
      children: [t(`This is to certify that the thesis entitled `), t(`"${thesis?.title || ''}"`, { bold: true }), t(` submitted by `), t(val('student_name') || '', { bold: true }), t(` (Registration No. ${val('reg_number') || ''}) in partial fulfilment of the requirements for the award of the Degree of `), t(`${val('degree') || 'Doctor of Philosophy'} in ${val('specialization') || ''}`, { bold: true }), t(` from `), t(val('university_name') || '', { bold: true }), t(` is a record of genuine and original research work carried out by the candidate under my supervision.`)],
      alignment: AlignmentType.JUSTIFIED,
      spacing:   { line: 480, before: 0, after: 480 },
      indent:    { firstLine: convertInchesToTwip(0.5) },
    }),
    new Paragraph({ children: [t('________________________'), new TextRun({ break: 1 }), t(val('supervisor_name') || '', { bold: true }), new TextRun({ break: 1 }), t(val('supervisor_designation') || ''), new TextRun({ break: 1 }), t(val('supervisor_institution') || ''), new TextRun({ break: 2 }), t('Date: ____________________')], spacing: { before: 720, after: 0 } }),
  ])

  // ── Declaration ───────────────────────────────────────────────────────────
  sections.push([
    new Paragraph({ children: [t('DECLARATION', { bold: true, size: 28, allCaps: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    new Paragraph({
      children: [t(`I, `), t(val('student_name') || '', { bold: true }), t(`, hereby declare that the thesis entitled `), t(`"${thesis?.title || ''}"`, { bold: true }), t(` submitted for the award of the Degree of `), t(`${val('degree') || 'Doctor of Philosophy'} in ${val('specialization') || ''}`, { bold: true }), t(` is my own original work. This thesis has not been submitted for the award of any other degree or diploma in any university.`)],
      alignment: AlignmentType.JUSTIFIED,
      spacing:   { line: 480, before: 0, after: 480 },
      indent:    { firstLine: convertInchesToTwip(0.5) },
    }),
    new Paragraph({ children: [t('Place: ' + (val('declaration_city') || '___________'), {}), new TextRun({ break: 3 }), t('________________________'), new TextRun({ break: 1 }), t(val('student_name') || '', { bold: true })], spacing: { before: 480, after: 0 } }),
  ])

  // ── Dedication (conditional) ──────────────────────────────────────────────
  if (val('dedication_text')) {
    sections.push([
      new Paragraph({ children: [t(fm.dedication_text, { italic: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 1440, after: 0 } }),
    ])
  }

  // ── Acknowledgements ──────────────────────────────────────────────────────
  if (val('acknowledgement_text')) {
    sections.push([
      new Paragraph({ children: [t('ACKNOWLEDGEMENTS', { bold: true, size: 28, allCaps: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
      new Paragraph({
        children:  [t(fm.acknowledgement_text)],
        alignment: AlignmentType.JUSTIFIED,
        spacing:   { line: 480, before: 0, after: 0 },
        indent:    { firstLine: convertInchesToTwip(0.5) },
      }),
    ])
  }

  // ── Abstract ──────────────────────────────────────────────────────────────
  const abstract = val('abstract_text')
  sections.push([
    new Paragraph({ children: [t('ABSTRACT', { bold: true, size: 28, allCaps: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    new Paragraph({
      children:  [t(abstract || '[Abstract not yet written]', abstract ? {} : { italic: true, color: 'FF0000' })],
      alignment: AlignmentType.JUSTIFIED,
      spacing:   { line: 480, before: 0, after: 240 },
      indent:    { firstLine: convertInchesToTwip(0.5) },
    }),
    ...(fm?.keywords?.length > 0 ? [
      new Paragraph({ children: [t('Keywords: ', { bold: true }), t(fm.keywords.join(', '))], spacing: { before: 120, after: 0 } }),
    ] : []),
  ])

  return sections.flat()
}

// ── Chapter section builder ───────────────────────────────────────────────────

async function buildChapterSection(ch, figureRegistry, tableRegistry, ordered = null) {
  const html      = sanitizeChapterContent(ch.content || '')
  const elements  = await htmlToDocxElements(html, figureRegistry, tableRegistry, ordered)
  const title     = `CHAPTER ${ch.chapter_number}: ${ch.title || ''}`.toUpperCase()

  return [
    new Paragraph({
      children:        [new TextRun({ text: title, font: TIMES_NEW_ROMAN, size: FONT_SIZE_H1, bold: true, allCaps: true })],
      heading:         HeadingLevel.HEADING_1,
      alignment:       AlignmentType.CENTER,
      pageBreakBefore: true,
      spacing:         { before: 240, after: 480 },
    }),
    ...elements,
  ]
}

// ── LOF / LOT builder ─────────────────────────────────────────────────────────

function buildListOfFigures(figures) {
  const entries = Array.isArray(figures) ? figures : [...figures.values()].filter(f => f.type !== 'table')
  if (entries.length === 0) return []

  return [
    new Paragraph({ children: [new TextRun({ text: 'LIST OF FIGURES', font: TIMES_NEW_ROMAN, size: FONT_SIZE_H1, bold: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    ...entries.map(f =>
      new Paragraph({
        children: [
          new TextRun({ text: `${f.displayLabel}: ${f.title || '(untitled)'}`, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY }),
          new TextRun({ text: '  ……', font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, color: 'AAAAAA' }),
        ],
        spacing: { after: 80 },
      })
    ),
  ]
}

function buildListOfTables(tables) {
  const entries = Array.isArray(tables) ? tables : [...tables.values()]
  if (entries.length === 0) return []

  return [
    new Paragraph({ children: [new TextRun({ text: 'LIST OF TABLES', font: TIMES_NEW_ROMAN, size: FONT_SIZE_H1, bold: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    ...entries.map(t =>
      new Paragraph({
        children: [
          new TextRun({ text: `${t.displayLabel}: ${t.title || '(untitled)'}`, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY }),
        ],
        spacing: { after: 80 },
      })
    ),
  ]
}

// ── TOC builder ───────────────────────────────────────────────────────────────

function buildTocSection(chapters, lof = [], lot = []) {
  const frontPages = ['Certificate', 'Declaration', 'Dedication', 'Acknowledgements', 'Abstract']
  if (lof.length > 0) frontPages.push('List of Figures')
  if (lot.length > 0) frontPages.push('List of Tables')

  return [
    new Paragraph({ children: [new TextRun({ text: 'TABLE OF CONTENTS', font: TIMES_NEW_ROMAN, size: FONT_SIZE_H1, bold: true })], alignment: AlignmentType.CENTER, pageBreakBefore: true, spacing: { before: 240, after: 480 } }),
    ...frontPages.map(p =>
      new Paragraph({ children: [new TextRun({ text: p, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold: false })], spacing: { before: 60, after: 20 } })
    ),
    ...chapters.map(ch =>
      new Paragraph({
        children: [new TextRun({ text: `Chapter ${ch.chapter_number}: ${ch.title || ''}`, font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold: true })],
        spacing: { before: 120, after: 40 },
      })
    ),
    new Paragraph({ children: [new TextRun({ text: 'References / Bibliography', font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY, bold: true })], spacing: { before: 120, after: 0 } }),
  ]
}

// ── Document styles ───────────────────────────────────────────────────────────

function buildStyles() {
  return {
    default: {
      document: {
        run: { font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY },
        paragraph: { spacing: { line: 480, lineRule: 'auto' } },
      },
    },
    paragraphStyles: [
      {
        id: 'Normal',
        name: 'Normal',
        run: { font: TIMES_NEW_ROMAN, size: FONT_SIZE_BODY },
        paragraph: { spacing: { line: 480, lineRule: 'auto' }, alignment: AlignmentType.JUSTIFIED },
      },
      {
        id: 'Heading1',
        name: 'heading 1',
        basedOn: 'Normal',
        next: 'Normal',
        run: { font: TIMES_NEW_ROMAN, size: FONT_SIZE_H1, bold: true, allCaps: true },
        paragraph: { alignment: AlignmentType.CENTER, spacing: { before: 240, after: 480 } },
      },
      {
        id: 'Heading2',
        name: 'heading 2',
        basedOn: 'Normal',
        next: 'Normal',
        run: { font: TIMES_NEW_ROMAN, size: FONT_SIZE_H2, bold: true },
        paragraph: { spacing: { before: 200, after: 120 } },
      },
      {
        id: 'Heading3',
        name: 'heading 3',
        basedOn: 'Normal',
        next: 'Normal',
        run: { font: TIMES_NEW_ROMAN, size: FONT_SIZE_H3, bold: true, italics: true },
        paragraph: { spacing: { before: 160, after: 80 } },
      },
    ],
  }
}

// ── Document core properties ─────────────────────────────────────────────────
// Left unset, the docx package writes its own defaults into the DOCX core
// properties, so a student opening File → Properties on their own thesis saw a
// library name in the Author field. Fill them from data we already hold.

function docProperties(thesis, fm) {
  const author = fm?.student_name || ''
  return {
    creator:        author || undefined,
    lastModifiedBy: author || undefined,
    title:          thesis?.title || undefined,
    description:    fm?.university_name || thesis?.university || undefined,
    subject:        thesis?.domain || undefined,
  }
}

// ── Single-chapter export (used by the per-chapter export dropdown) ──────────
// No thesis-wide composition/registry available here, so figures fall back to
// their raw title/caption instead of an authoritative "Figure 3.2" number —
// same figure-rendering logic (real tables, embedded chart-svg PNGs) as the
// full-thesis export, just without cross-chapter numbering.

export async function generateSingleChapterDocxBlob(chapter, { thesis, fm } = {}) {
  const emptyRegistry = new Map()
  const section = await buildChapterSection(chapter, emptyRegistry, emptyRegistry)
  const doc = new Document({
    ...docProperties(thesis, fm),
    styles: buildStyles(),
    sections: [{
      properties: { page: { margin: MARGIN } },
      children:   section,
    }],
  })
  return Packer.toBlob(doc)
}

// ── Main export function ──────────────────────────────────────────────────────

/**
 * Generate a real .docx Blob from thesis data.
 *
 * @param {{ thesis, chapters, fm, figures: Array, tables: Array }} data
 * @returns {Promise<Blob>}
 */
export async function generateDocxBlob({ thesis, chapters, fm, dbFigures = [] }) {
  // Compose document: assigns Figure X.Y / Table X.Y by final document position
  const composed = composeThesisDocument({ thesis, chapters, frontMatter: fm || {} })
  const { figureRegistry: figures, tableRegistry: tables, chapters: composedChs } = composed

  // Map composed chapters back to original chapter objects for the section builder
  const sorted = composedChs.map(c => ({ ...chapters.find(ch => ch.id === c.id) || {}, chapter_number: c.chapterNumber, title: c.title, content: c.sanitizedHtml, _figures: c.figures, _tables: c.tables }))

  // ── Build all children ─────────────────────────────────────────────────
  const frontMatterElements = buildFrontMatterParagraphs(fm, thesis)
  const tocElements         = buildTocSection(sorted, composed.lof, composed.lot)
  // composed.lof/lot are per-occurrence and in document order; the Maps are
  // keyed by colliding token ids and silently lose duplicates.
  const lofElements         = buildListOfFigures(composed.lof)
  const lotElements         = buildListOfTables(composed.lot)
  const chapterSections     = await Promise.all(sorted.map(ch => buildChapterSection(ch, figures, tables, { figures: ch._figures, tables: ch._tables })))
  const chapterElements     = chapterSections.flat()

  // ── Assemble document ──────────────────────────────────────────────────
  const doc = new Document({
    ...docProperties(thesis, fm),
    styles: buildStyles(),
    numbering: { config: [] },
    sections: [
      {
        properties: {
          page: {
            margin: MARGIN,
            pageNumbers: { start: 1, formatType: NumberFormat.DECIMAL },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: thesis?.title || 'PhD Thesis', font: TIMES_NEW_ROMAN, size: 18, color: '888888' }),
                ],
                alignment: AlignmentType.RIGHT,
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ children: [PageNumber.CURRENT], font: TIMES_NEW_ROMAN, size: 20 }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        children: [
          ...frontMatterElements,
          ...tocElements,
          ...lofElements,
          ...lotElements,
          ...chapterElements,
        ],
      },
    ],
  })

  const buffer = await Packer.toBlob(doc)
  return buffer
}

/**
 * Trigger browser download of the generated DOCX.
 */
export async function downloadDocx({ thesis, chapters, fm, dbFigures }) {
  const blob     = await generateDocxBlob({ thesis, chapters, fm, dbFigures })
  const safe     = (thesis?.title || 'thesis').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)
  const filename = `${safe}-thesis.docx`
  const url      = URL.createObjectURL(blob)
  const a        = document.createElement('a')
  a.href         = url
  a.download     = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return filename
}
